import { inject } from '@angular/core';
import {
  CanActivateChildFn,
  CanActivateFn,
  Router,
  UrlTree,
} from '@angular/router';
import { AuthService } from '../../features/auth/services/auth.service';
import { isPublicAuthRoute } from '../helpers/public-auth-route.helper';
import {
  hasAnyRole,
  rolesFromAccessToken,
} from '../helpers/jwt-payload.helper';
import {
  destinationForRoles,
  navigateToDestination,
} from '../helpers/app-destination.helper';

function resolveLayoutRoles(route: {
  data: Record<string, unknown>;
  parent: { data: Record<string, unknown> } | null;
}): string[] {
  let current: { data: Record<string, unknown>; parent: unknown } | null =
    route as { data: Record<string, unknown>; parent: unknown };
  while (current) {
    const roles = current.data['layoutRoles'];
    if (Array.isArray(roles) && roles.length > 0) {
      return roles.filter((role): role is string => typeof role === 'string');
    }
    current = current.parent as typeof current;
  }
  return [];
}

/**
 * Protects an app shell (family/school/admin/patrol):
 * - public /auth* is allowed when logged out
 * - logged-in users with a matching layout role may enter
 * - wrong-role users are sent to their own app (cross-subdomain when needed)
 * - logged-in correct-role users hitting /auth are sent to their home
 */
export const layoutRoleGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const layoutRoles = resolveLayoutRoles(route);
  const token = auth.accessToken();
  const userRoles = rolesFromAccessToken(token);
  const onAuth = isPublicAuthRoute(state.url);

  if (!token) {
    return onAuth ? true : router.createUrlTree(['/auth']);
  }

  if (hasAnyRole(userRoles, layoutRoles)) {
    if (onAuth) {
      const home = destinationForRoles(userRoles);
      return home ? router.createUrlTree([home.path]) : true;
    }
    return true;
  }

  // Authenticated but wrong app — bounce to the app that matches their roles.
  const destination = destinationForRoles(userRoles);
  if (destination) {
    navigateToDestination(destination);
    return false;
  }

  auth.clearSession();
  return onAuth ? true : router.createUrlTree(['/auth']);
};

export const layoutRoleChildGuard: CanActivateChildFn = (route, state) =>
  layoutRoleGuard(route, state);
